// Recharts-based charts used on the overview and trends tabs.
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  COLORS,
  RATING_COLORS,
  SENTIMENT_BY_KEY,
  SENTIMENTS,
} from "./constants";
import { ChartTooltip, EmptyState, TooltipRow } from "./ui";
import { nf, pct } from "./utils";

export const RatingDistributionList = ({ distribution, previous, onSelect }) => {
  const prevByRating = Object.fromEntries(
    (previous || []).map((r) => [r.rating, r]),
  );
  const rows = [...distribution].sort((a, b) => b.rating - a.rating);
  return (
    <ul className="gr-ratings">
      {rows.map((r) => (
        <li key={r.rating}>
          <button
            type="button"
            onClick={() => onSelect(String(r.rating))}
            title={`Pokaż opinie z oceną ${r.rating}`}
          >
            <span className="gr-ratings-label">
              {r.rating} <span aria-hidden="true">★</span>
            </span>
            <span className="gr-ratings-track">
              <span
                style={{
                  width: `${r.percentage}%`,
                  background: RATING_COLORS[r.rating],
                }}
              />
            </span>
            <span className="gr-ratings-value">{nf(r.count)}</span>
            <span className="gr-ratings-pct">
              {nf(r.percentage, 1)}%
              {prevByRating[r.rating] && (
                <small>
                  poprz. {nf(prevByRating[r.rating].percentage, 1)}%
                </small>
              )}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
};

export const VolumeChart = ({ timeline, onSelectBucket, height = 280 }) => {
  if (!timeline.length) return <EmptyState title="Brak danych w czasie" />;
  return (
    <div className="gr-chart">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={timeline}
          margin={{ top: 8, right: 8, bottom: 0, left: -18 }}
          onClick={(state) =>
            state?.activePayload?.[0] &&
            onSelectBucket(state.activePayload[0].payload)
          }
          barCategoryGap="18%"
        >
          <CartesianGrid vertical={false} stroke={COLORS.grid} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={{ stroke: "#c3c2b7" }}
            minTickGap={16}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            allowDecimals={false}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11 }}
          />
          <Tooltip
            cursor={{ fill: "rgba(42,120,214,0.08)" }}
            content={
              <ChartTooltip
                render={(d) => (
                  <>
                    <div className="gr-tooltip-title">{d.label}</div>
                    <TooltipRow label="Opinie" value={nf(d.count)} />
                    <TooltipRow
                      label="Średnia ocena"
                      value={
                        d.average_rating ? `${nf(d.average_rating, 2)} ★` : "–"
                      }
                    />
                    {SENTIMENTS.map((s) => (
                      <TooltipRow
                        key={s.key}
                        color={s.color}
                        label={s.label}
                        value={nf(d[s.field])}
                      />
                    ))}
                    {d.count > 0 && (
                      <div className="gr-tooltip-hint">
                        Kliknij, aby zobaczyć opinie
                      </div>
                    )}
                  </>
                )}
              />
            }
          />
          <Bar
            dataKey="positive_count"
            name="Pozytywne"
            stackId="s"
            fill={COLORS.positive}
            stroke="#fff"
            strokeWidth={1}
            cursor="pointer"
            animationDuration={400}
          />
          <Bar
            dataKey="neutral_count"
            name="Neutralne"
            stackId="s"
            fill={COLORS.neutral}
            stroke="#fff"
            strokeWidth={1}
            cursor="pointer"
            animationDuration={400}
          />
          <Bar
            dataKey="negative_count"
            name="Negatywne"
            stackId="s"
            fill={COLORS.negative}
            stroke="#fff"
            strokeWidth={1}
            radius={[3, 3, 0, 0]}
            cursor="pointer"
            animationDuration={400}
          />
        </BarChart>
      </ResponsiveContainer>
      <div className="gr-legend">
        {SENTIMENTS.map((s) => (
          <span key={s.key}>
            <i style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
};

export const MetricLineChart = ({
  timeline,
  dataKey,
  color,
  domain,
  reference,
  formatter,
  name,
  height = 240,
}) => {
  const hasData = timeline.some(
    (t) => t[dataKey] !== null && t[dataKey] !== undefined,
  );
  if (!hasData) return <EmptyState title="Brak danych w czasie" />;
  return (
    <div className="gr-chart">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={timeline}
          margin={{ top: 8, right: 12, bottom: 0, left: -18 }}
        >
          <CartesianGrid vertical={false} stroke={COLORS.grid} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={{ stroke: "#c3c2b7" }}
            minTickGap={16}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            domain={domain}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11 }}
            tickFormatter={(v) => nf(v, 1)}
          />
          {reference !== undefined && reference !== null && (
            <ReferenceLine
              y={reference}
              stroke="#898781"
              strokeDasharray="4 4"
              label={{
                value: `śr. ${nf(reference, 2)}`,
                position: "insideTopRight",
                fontSize: 11,
                fill: "#52514e",
              }}
            />
          )}
          <Tooltip
            content={
              <ChartTooltip
                render={(d) => (
                  <>
                    <div className="gr-tooltip-title">{d.label}</div>
                    <TooltipRow
                      color={color}
                      label={name}
                      value={
                        d[dataKey] !== null
                          ? formatter(d[dataKey])
                          : "brak opinii"
                      }
                    />
                    <TooltipRow label="Liczba opinii" value={nf(d.count)} />
                  </>
                )}
              />
            }
          />
          <Line
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={2}
            dot={{ r: 3, strokeWidth: 0, fill: color }}
            activeDot={{ r: 5 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export const SentimentDonut = ({ kpis, onSelect }) => {
  const data = [
    { key: "pozytywna", value: kpis.positive_reviews },
    { key: "neutralna", value: kpis.neutral_reviews },
    { key: "negatywna", value: kpis.negative_reviews },
  ].filter((d) => d.value > 0);
  return (
    <div className="gr-donut">
      <div className="gr-donut-chart">
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="key"
              innerRadius="66%"
              outerRadius="96%"
              paddingAngle={data.length > 1 ? 2 : 0}
              stroke="none"
              isAnimationActive={false}
            >
              {data.map((d) => (
                <Cell
                  key={d.key}
                  fill={SENTIMENT_BY_KEY[d.key].color}
                  cursor="pointer"
                  onClick={() => onSelect(d.key)}
                />
              ))}
            </Pie>
            <Tooltip
              content={
                <ChartTooltip
                  render={(d) => (
                    <TooltipRow
                      color={SENTIMENT_BY_KEY[d.key].color}
                      label={SENTIMENT_BY_KEY[d.key].label}
                      value={`${nf(d.value)} (${nf(pct(d.value, kpis.total_reviews), 1)}%)`}
                    />
                  )}
                />
              }
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="gr-donut-center">
          <strong>{nf(kpis.positive_share, 0)}%</strong>
          <span>pozytywnych</span>
        </div>
      </div>
      <ul className="gr-donut-legend">
        {SENTIMENTS.map((s) => {
          const value =
            s.key === "pozytywna"
              ? kpis.positive_reviews
              : s.key === "negatywna"
                ? kpis.negative_reviews
                : kpis.neutral_reviews;
          return (
            <li key={s.key}>
              <button type="button" onClick={() => onSelect(s.key)}>
                <i style={{ background: s.color }} />
                <span>{s.label}</span>
                <strong>{nf(value)}</strong>
                <small>{nf(pct(value, kpis.total_reviews), 1)}%</small>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="gr-donut-foot">
        Średni wynik sentymentu (AI):{" "}
        <strong>{nf(kpis.average_sentiment_score, 1)} / 10</strong>
      </div>
    </div>
  );
};
